import { Button, Heading, Spinner, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useNavAccess } from "../../common/layout/useNavAccess";
import { revealTabInRow } from "../../common/scroll";
import { useRestoreScrollOnBack } from "../../common/layout/useRestoreScrollOnBack";
import { ContractsOverview } from "./ContractsOverview";
import { NeedsOverview } from "./NeedsOverview";
import { ProcurementPlan } from "./ProcurementPlan";
import { ProcurementsOverview } from "./ProcurementsOverview";
import { ProjectsOverview } from "./ProjectsOverview";
import { SavingsOverview } from "./SavingsOverview";
import { TasksPerOwner } from "./TasksPerOwner";

type OverviewTab =
  | "contracts"
  | "tasks-per-owner"
  | "procurements"
  | "procurement-plan"
  | "projects"
  | "savings"
  | "needs";

export const OverviewPage = () => {
  const { t } = useTranslation();
  const { access, resolving } = useNavAccess();
  const [params, setParams] = useSearchParams();
  useRestoreScrollOnBack();
  const tabs: { key: OverviewTab; label: string; panel: JSX.Element }[] = [
    ...(access.canViewContractOverview
      ? [
          { key: "contracts" as const, label: t("Contracts"), panel: <ContractsOverview /> },
          { key: "tasks-per-owner" as const, label: t("Tasks per owner"), panel: <TasksPerOwner /> },
        ]
      : []),
    ...(access.canViewSourcingEvents && !access.isSupplier
      ? [
          { key: "procurements" as const, label: t("Procurements"), panel: <ProcurementsOverview /> },
          { key: "procurement-plan" as const, label: t("Procurement plan"), panel: <ProcurementPlan /> },
        ]
      : []),
    ...(access.canViewProjects
      ? [{ key: "projects" as const, label: t("Projects"), panel: <ProjectsOverview /> }]
      : []),
    ...(access.canViewContractOverview && !access.isSupplier
      ? [{ key: "savings" as const, label: t("Savings"), panel: <SavingsOverview /> }]
      : []),
    ...(access.canViewNeedsOverview ? [{ key: "needs" as const, label: t("Needs"), panel: <NeedsOverview /> }] : []),
  ];
  const tabIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.key === params.get("tab")),
  );
  const tabListRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (tabListRef.current) revealTabInRow(tabListRef.current, tabIndex);
  }, [tabIndex, resolving]);
  const openTab = (index: number) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams({ tab: tabs[index].key });
        const kind = current.get("kind");
        if (index === tabIndex && kind) updated.set("kind", kind);
        return updated;
      },
      { replace: true },
    );

  return (
    <Stack spacing="6">
      <Heading size="lg" color="smText">
        {t("Overview")}
      </Heading>
      {resolving ? (
        <Spinner color="smPrimary" aria-label={t("Loading the overview") ?? undefined} />
      ) : tabs.length === 0 ? (
        <Stack spacing="3" align="flex-start" data-testid="overview-nothing-to-show">
          <Text color="smMuted">
            {t("Your access in this organisation does not include contracts or procurements to show an overview of.")}
          </Text>
          <Button as={RouterLink} to="/" variant="outline" colorScheme="teal" size="sm">
            {t("Go to the home page")}
          </Button>
        </Stack>
      ) : (
        <Tabs colorScheme="teal" isLazy index={tabIndex} onChange={openTab}>
          <TabList ref={tabListRef} overflowX="auto" overflowY="hidden">
            {tabs.map((tab) => (
              <Tab key={tab.key} flexShrink="0" whiteSpace="nowrap">
                {tab.label}
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {tabs.map((tab) => (
              <TabPanel key={tab.key} px="0">
                {tab.panel}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}
    </Stack>
  );
};
